/**
 * QuestionnaireResponse schemas — clinical notes, assessments, forms, treatment plans.
 *
 * Enum arrays are tied to fhir-model types via `satisfies`.
 */
import { z } from 'zod';
import type {
  QRStatus as FhirQRStatus,
  DetailedStatusCode as FhirDetailedStatus,
} from '@unify/fhir-model/resources/questionnaire-response';

/** FHIR QR status values — synced with fhir-model. */
export const qrStatusValues = [
  'in-progress',
  'completed',
  'amended',
  'stopped',
] as const satisfies readonly FhirQRStatus[];

/** Unify detailed-status codes — synced with fhir-model. */
export const detailedStatusValues = [
  'draft',
  'pending-signature',
  'signed',
  'pending-cosign',
  'cosigned',
  'needs-edits',
  'amended',
] as const satisfies readonly FhirDetailedStatus[];

/**
 * Base QR write payload — common to all QR creation paths.
 */
export const baseQuestionnaireResponseSchema = z.object({
  status: z.enum(qrStatusValues),
  questionnaireCanonical: z.string().min(1, 'Questionnaire reference is required'),
  patientId: z.string().min(1, 'Patient is required'),
  authorReference: z.string().optional(),
  authorDisplay: z.string().optional(),
  authored: z.string().optional(),
  episodeOfCareId: z.string().optional(),
  appointmentId: z.string().optional(),
  detailedStatus: z.enum(detailedStatusValues).optional(),
});

export type BaseQuestionnaireResponseInput = z.infer<typeof baseQuestionnaireResponseSchema>;

/**
 * Clinical note save — additional fields beyond the base QR.
 */
export const clinicalNoteSaveSchema = baseQuestionnaireResponseSchema.extend({
  encounterId: z.string().optional(),
  templateCategory: z.string().optional(),
  serviceTypeTag: z.string().optional(),
});

export type ClinicalNoteSaveInput = z.infer<typeof clinicalNoteSaveSchema>;

/** Signature info for provider/supervisor sign-off. */
export const signatureSchema = z.object({
  signerReference: z.string().min(1),
  signerDisplay: z.string().optional(),
  signedAt: z.string().min(1),
  ipAddress: z.string().optional(),
  typedText: z.string().optional(),
});

export type SignatureInput = z.infer<typeof signatureSchema>;

/** Client portal captured signature (typed or drawn). */
export const clientPortalSignatureSchema = z
  .object({
    name: z.string(),
    title: z.string().optional(),
    license: z.string().optional(),
    signedAt: z.string().optional(),
    ipAddress: z.string().optional(),
    signatureData: z.string().optional(),
    signatureFormat: z.string().optional(),
    signatureMode: z.enum(['type', 'draw']).optional(),
    typedText: z.string().optional(),
  })
  .refine(
    (sig) => Boolean(sig.signatureData?.trim() || sig.name.trim()),
    { message: 'Signature is required' },
  );

export type ClientPortalSignatureInput = z.infer<typeof clientPortalSignatureSchema>;

/** Review comment on a QR (supervision workflow). */
export const reviewCommentSchema = z.object({
  authorReference: z.string().min(1),
  authorDisplay: z.string().optional(),
  text: z.string().min(1, 'Comment text is required'),
  timestamp: z.string().min(1),
  resolved: z.boolean().optional(),
  resolvedBy: z.string().optional(),
  resolvedAt: z.string().optional(),
});

export type ReviewCommentInput = z.infer<typeof reviewCommentSchema>;
